// src/components/Admin/ReservationsList.js
import React, { useEffect, useState } from "react";
import { db } from "../../api";
import { collection, onSnapshot, doc, updateDoc } from "../../api";

export default function ReservationsList() {
  const [reservations, setReservations] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "reservations"), (snapshot) => {
      const results = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setReservations(results);
    });
    return () => unsub();
  }, []);

  const handleStatusUpdate = async (reservationId, newStatus, itemId) => {
    try {
      const updates = {status: newStatus};
      const docRef = doc(db, "reservations", reservationId);
      await updateDoc(docRef, updates);
    } catch (err) {
      console.error(err);
    }
  };
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      case 'active': return 'text-blue-600 bg-blue-100';
      case 'completed': return 'text-green-600 bg-green-100';
      case 'cancelled': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };
  
  const formatDate = (date) => {
    if (!date) return 'N/A';
    if (date.toDate) {
      return date.toDate().toLocaleDateString();
    }
    return new Date(date).toLocaleDateString();
  };
  
  const filteredReservations = reservations.filter(res => {
    const matchesStatus = filterStatus === 'all' || res.status === filterStatus;
    const matchesSearch = searchTerm === '' || 
      res.itemName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      res.category?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });


  return (
    <div className="flex flex-col space-y-4">
      {/* Filters */}
      <div className="bg-white border border-gray-300 rounded shadow p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <label className="text-sm font-medium text-navy mb-1 block">
              Search:
            </label>
            <input
              type="text"
              placeholder="Search by item name or category..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full border border-gray-300 rounded p-2 focus:outline-none focus:border-burgundy"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-navy mb-1 block">
              Filter by Status:
            </label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="border border-gray-300 rounded p-2 focus:outline-none focus:border-burgundy"
            >
              <option value="all">All</option>
              <option value="pending">Pending</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>
      </div>
      
      {/* Reservations List */}
      {filteredReservations.length === 0 ? (
        <p className="text-navy text-center py-8">No reservations found matching your criteria.</p>
      ) : (
        filteredReservations.map((res) => (
          <div
            key={res.id}
            className="bg-white border border-gray-300 rounded shadow p-4"
          >
            <div className="flex justify-between items-start mb-4">
              <div className="flex-1">
                <h4 className="text-lg font-semibold text-burgundy mb-2">
                  {res.itemName || 'Unknown Item'}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                  <p><strong>Category:</strong> {res.category}</p>
                  <p><strong>Customer:</strong> {res.userId}</p>
                  <p><strong>Start Date:</strong> {formatDate(res.startDate)}</p>
                  <p><strong>End Date:</strong> {formatDate(res.endDate)}</p>
                  <p><strong>Daily Rate:</strong> ${res.dailyRate || 0}</p>
                  <p><strong>Total Cost:</strong> ${res.totalCost || 0}</p>
                  {res.size && <p><strong>Size:</strong> {res.size}</p>}
                  {res.customerNotes && (
                    <p className="md:col-span-2"><strong>Notes:</strong> {res.customerNotes}</p>
                  )}
                </div>
              </div>
              <div className="ml-4">
                <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(res.status || 'pending')}`}>
                  {(res.status || 'pending').toUpperCase()}
                </span>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {res.status === 'pending' && (
                <>
                  <button
                    onClick={() => handleStatusUpdate(res.id, "active", res.itemId)}
                    className="bg-blue-600 text-white py-1 px-3 rounded hover:bg-blue-700 transition text-sm"
                  >
                    Mark as Picked Up
                  </button>
                  <button
                    onClick={() => handleStatusUpdate(res.id, "cancelled")}
                    className="bg-red-600 text-white py-1 px-3 rounded hover:bg-red-700 transition text-sm"
                  >
                    Cancel
                  </button>
                </>
              )}
              {res.status === 'active' && (
                <button
                  onClick={() => handleStatusUpdate(res.id, "completed", res.itemId)}
                  className="bg-green-600 text-white py-1 px-3 rounded hover:bg-green-700 transition text-sm"
                >
                  Mark as Returned
                </button>
              )}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
